/**
 * PDP Accessory Hero — Swiper gallery + variant switching + ATC.
 *
 * Slimmer cousin of pdp-hero.js — no subscribe/one-time toggle, no caffeine
 * pill repaint, no testimonial carousel. Adds a − / qty / + stepper that
 * binds to the hidden quantity input.
 *
 * Variant switching uses the same Section Rendering API pattern as the
 * main PDP hero: fetch /products/{handle}?variant={id}&section_id={id},
 * swap the hero innerHTML, re-init Swiper.
 *
 * ATC: AJAX POST /cart/add.js → fire cancellable 'pdp:atc-success' event
 * (the upsell modal listens to this) → openUpCart() on resume. Per memory
 * rule "ATC must use UpCart slide cart" — never traditional form submit.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Swiper gallery
   * ------------------------------------------------------------------ */
  function initGalleries(root) {
    const scope = root || document;
    if (typeof Swiper === 'undefined') {
      console.warn('[pdp-accessory-hero] Swiper not loaded — gallery will stack.');
      return;
    }
    scope.querySelectorAll('[data-pdp-accessory-hero]').forEach(function (hero) {
      const mainEl = hero.querySelector('[data-pdp-gallery-main]');
      const thumbsEl = hero.querySelector('[data-pdp-gallery-thumbs]');
      const prevBtn = hero.querySelector('[data-pdp-gallery-prev]');
      const nextBtn = hero.querySelector('[data-pdp-gallery-next]');
      if (!mainEl) return;

      // Skip if already initialized (Swiper sets a .swiper-initialized class).
      if (mainEl.classList.contains('swiper-initialized')) return;

      let thumbsSwiper = null;
      if (thumbsEl) {
        thumbsSwiper = new Swiper(thumbsEl, {
          slidesPerView: 5,
          spaceBetween: 8,
          watchSlidesProgress: true,
          slideToClickedSlide: true,
          freeMode: { enabled: true, sticky: true },
          breakpoints: {
            750: { slidesPerView: 6, spaceBetween: 10 }
          }
        });
      }

      new Swiper(mainEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 300,
        navigation: prevBtn && nextBtn ? { prevEl: prevBtn, nextEl: nextBtn } : false,
        thumbs: thumbsSwiper ? { swiper: thumbsSwiper } : undefined,
        keyboard: { enabled: true },
        a11y: {
          prevSlideMessage: 'Previous product image',
          nextSlideMessage: 'Next product image'
        }
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Quantity stepper
   * ------------------------------------------------------------------ */
  function initQtyStepper(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-pdp-accessory-hero] [data-pdp-qty-wrap]').forEach(function (wrap) {
      const input = wrap.querySelector('[data-pdp-qty-input]');
      const hiddenQty = wrap.closest('form').querySelector('[data-pdp-buybox-qty]');
      const dec = wrap.querySelector('[data-pdp-qty-dec]');
      const inc = wrap.querySelector('[data-pdp-qty-inc]');
      if (!input) return;

      function syncHidden() {
        const n = Math.max(1, parseInt(input.value, 10) || 1);
        input.value = String(n);
        if (hiddenQty) hiddenQty.value = String(n);
      }

      if (dec) dec.addEventListener('click', function () {
        const n = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
        input.value = String(n);
        syncHidden();
      });
      if (inc) inc.addEventListener('click', function () {
        const n = Math.max(1, (parseInt(input.value, 10) || 1) + 1);
        input.value = String(n);
        syncHidden();
      });
      input.addEventListener('change', syncHidden);
      input.addEventListener('input', syncHidden);
      syncHidden();
    });
  }

  /* ------------------------------------------------------------------ *
   * UpCart slide cart
   * ------------------------------------------------------------------ */
  function openUpCart() {
    if (typeof window.upcartOpenCart === 'function') {
      window.upcartOpenCart();
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('upcart:open', { bubbles: true }));
    console.warn('[pdp-accessory-hero] UpCart drawer not available — dispatched cart:refresh fallback.');
  }

  /* ------------------------------------------------------------------ *
   * ATC — AJAX submit with cancellable upsell event
   * ------------------------------------------------------------------ */
  function initBuyBox(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-pdp-accessory-hero] [data-pdp-buybox]').forEach(function (form) {
      if (form.dataset.atcWired === '1') return;
      form.dataset.atcWired = '1';

      const submitBtn = form.querySelector('[data-atc-button]');
      const submitText = form.querySelector('.pdp-buybox__submit-text');
      const errorSlot = form.querySelector('[data-pdp-buybox-error]');

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!submitBtn || submitBtn.disabled) return;

        if (errorSlot) {
          errorSlot.hidden = true;
          errorSlot.textContent = '';
        }
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-busy', 'true');
        if (submitText) submitText.textContent = 'ADDING…';

        const formData = new FormData(form);

        const cartAddUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
          ? window.Shopify.routes.root + 'cart/add.js'
          : '/cart/add.js';

        fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
          credentials: 'same-origin'
        })
          .then(function (res) {
            return res.json().then(function (body) { return { ok: res.ok, status: res.status, body: body }; });
          })
          .then(function (result) {
            if (!result.ok) {
              const message = (result.body && (result.body.description || result.body.message)) ||
                'Could not add to cart — please try again.';
              if (errorSlot) { errorSlot.textContent = message; errorSlot.hidden = false; }
              return;
            }
            function resume() {
              openUpCart();
              if (window.PUB_SUB_EVENTS && window.publish) {
                window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'pdp-accessory-hero', productVariantId: formData.get('id') });
              }
            }
            const upsellEvent = new CustomEvent('pdp:atc-success', {
              cancelable: true,
              bubbles: true,
              detail: {
                addedVariantId: formData.get('id'),
                cartResponse: result.body,
                atcButton: submitBtn,
                resume: resume
              }
            });
            const notCancelled = document.dispatchEvent(upsellEvent);
            if (notCancelled) resume();
          })
          .catch(function (err) {
            console.error('[pdp-accessory-hero] ATC error', err);
            if (errorSlot) {
              errorSlot.textContent = 'Could not add to cart — please try again.';
              errorSlot.hidden = false;
            }
          })
          .finally(function () {
            submitBtn.disabled = false;
            submitBtn.removeAttribute('aria-busy');
            if (submitText) submitText.textContent = 'ADD TO CART';
          });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Variant switching via Section Rendering API
   * ------------------------------------------------------------------ */
  function initVariantSwitcher(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-pdp-accessory-hero]').forEach(function (hero) {
      const tiles = hero.querySelectorAll('[data-pdp-variant-tile]');
      if (!tiles || tiles.length < 2) return;

      tiles.forEach(function (tile) {
        tile.addEventListener('click', function (e) {
          e.preventDefault();
          if (tile.getAttribute('aria-disabled') === 'true') return;
          const variantId = tile.getAttribute('data-variant-id');
          const fallbackUrl = tile.getAttribute('data-variant-url');
          if (!variantId) return;
          swapVariant(hero, variantId, fallbackUrl);
        });

        // Keyboard support for radiogroup: arrow keys move focus + activate.
        tile.addEventListener('keydown', function (e) {
          const tileList = Array.from(hero.querySelectorAll('[data-pdp-variant-tile]'));
          const idx = tileList.indexOf(tile);
          let next = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            next = tileList[(idx + 1) % tileList.length];
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            next = tileList[(idx - 1 + tileList.length) % tileList.length];
          }
          if (next) {
            e.preventDefault();
            next.focus();
            next.click();
          }
        });
      });
    });
  }

  function swapVariant(hero, variantId, fallbackUrl) {
    const productUrl = hero.getAttribute('data-product-url');
    const sectionId = hero.getAttribute('data-section-id');
    if (!productUrl || !sectionId) {
      if (fallbackUrl) window.location.href = fallbackUrl;
      return;
    }
    hero.setAttribute('aria-busy', 'true');
    const url = productUrl + '?variant=' + encodeURIComponent(variantId) + '&section_id=' + encodeURIComponent(sectionId);
    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Section render failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        // The section renders its outer wrapper too, so replace the whole hero.
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-pdp-accessory-hero]');
        if (!fresh) throw new Error('Could not find rendered hero in response');
        hero.replaceWith(fresh);
        // Update URL without full nav.
        if (window.history && window.history.replaceState) {
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.set('variant', variantId);
          window.history.replaceState({}, '', newUrl.toString());
        }
        // Re-init Swiper / qty stepper / buy box on the fresh DOM.
        initGalleries(fresh.parentNode);
        initQtyStepper(fresh.parentNode);
        initBuyBox(fresh.parentNode);
        initVariantSwitcher(fresh.parentNode);
        // Publish variant-change so cart drawer, dynamic checkout, etc. update.
        if (window.PUB_SUB_EVENTS && window.publish) {
          window.publish(window.PUB_SUB_EVENTS.variantChange, {
            data: { variant: { id: parseInt(variantId, 10) }, sectionId: sectionId }
          });
        }
      })
      .catch(function (err) {
        console.error('[pdp-accessory-hero] Variant swap failed, falling back to navigation', err);
        if (fallbackUrl) window.location.href = fallbackUrl;
      });
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  function boot() {
    initGalleries(document);
    initQtyStepper(document);
    initBuyBox(document);
    initVariantSwitcher(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
